import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { Btn, Badge, Loading, EmptyState } from '../components/ui';

function ScenarioCard({ scenario, selected, onClick }) {
  const eff = parseFloat(scenario.efficiency || 0);
  const effColor = eff >= 75 ? 'var(--grn)' : eff >= 65 ? 'var(--amb)' : 'var(--red)';
  return (
    <div
      onClick={onClick}
      style={{
        minWidth: 110, border: selected ? '2px solid var(--acc)' : '0.5px solid var(--border-md)',
        borderRadius: 10, padding: '10px 13px', cursor: 'pointer', flexShrink: 0,
        background: selected ? 'var(--acc-bg)' : 'var(--bg-primary)', transition: 'all 0.15s'
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 500, color: selected ? 'var(--acc)' : 'var(--text-primary)' }}>{scenario.manpower}</div>
      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 6 }}>operators</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: effColor }}>{eff.toFixed(1)}%</div>
      <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{scenario.target_output_day} pcs/day</div>
    </div>
  );
}

function ScenarioDetail({ scenario, onSave, onAssign, onManual, saving }) {
  if (!scenario) return null;
  const eff = parseFloat(scenario.efficiency || 0);

  const exportExcel = () => {
    const rows = scenario.items.map(item => ({
      '#': item.row_number, 'Operation': item.operation_name,
      'SAM': parseFloat(item.sam).toFixed(3), 'Operators': item.ops_count,
      'Output/hr': Math.round(item.output_hr), 'Skill Req%': item.skill_req_pct,
      'Machine': item.machine_type || '—', 'Bottleneck': item.is_bottleneck ? 'Yes' : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Line Design');
    XLSX.writeFile(wb, `line-design-${scenario.manpower}op.xlsx`);
  };

  const machineMap = {};
  scenario.items.forEach(it => { if (it.machine_type) machineMap[it.machine_type] = (machineMap[it.machine_type] || 0) + Math.ceil(parseFloat(it.ops_count) || 0); });
  const groupMap = {};
  scenario.items.forEach(it => { const g = it.group_name || 'Other'; groupMap[g] = (groupMap[g] || 0) + (parseFloat(it.ops_count) || 0); });

  return (
    <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '0.5px solid var(--border-md)', background: 'var(--bg-secondary)' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Scenario: {scenario.manpower} operators</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
            Bottleneck: <strong>{scenario.bottleneck_operation}</strong> · Output: <strong>{scenario.target_output_day} pcs/day</strong> · Efficiency: <strong style={{ color: eff >= 75 ? 'var(--grn)' : eff >= 65 ? 'var(--amb)' : 'var(--red)' }}>{eff.toFixed(1)}%</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" onClick={exportExcel}>Export Excel</Btn>
          <Btn size="sm" onClick={onManual}>Manual override</Btn>
          <Btn size="sm" onClick={onSave} disabled={saving} variant="primary">{saving ? 'Saving…' : 'Save scenario'}</Btn>
          <Btn size="sm" variant="primary" onClick={onAssign}>Assign line →</Btn>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              {['#', 'Operation', 'SAM', 'Operators', 'Output/hr', 'Skill req%', 'Machine'].map((h, i) => (
                <th key={i} style={{ padding: '7px 10px', borderBottom: '0.5px solid var(--border-md)', textAlign: i === 0 ? 'center' : 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scenario.items.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '0.5px solid var(--border)', background: item.is_bottleneck ? '#FFF5F5' : '' }}>
                <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: 10.5, color: 'var(--text-tertiary)' }}>{idx + 1}</td>
                <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                  {item.is_bottleneck && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E24B4A', display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} />}
                  {item.is_clubbed && item.clubbed_partner_name ? `${item.operation_name} + ${item.clubbed_partner_name}` : item.operation_name}
                  {item.is_clubbed && <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--acc-bg)', color: 'var(--acc)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>🔗 Clubbed</span>}
                  {item.is_bottleneck && <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--red-bg)', color: 'var(--red)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>Bottleneck</span>}
                </td>
                <td style={{ padding: '7px 10px' }}>{parseFloat(item.sam).toFixed(2)}</td>
                <td style={{ padding: '7px 10px', fontWeight: 500, textAlign: 'center' }}>{item.ops_count}</td>
                <td style={{ padding: '7px 10px', color: item.is_bottleneck ? 'var(--red)' : 'var(--text-primary)', fontWeight: item.is_bottleneck ? 500 : 400 }}>{Math.round(item.output_hr)}/hr</td>
                <td style={{ padding: '7px 10px' }}>
                  <span style={{
                    display: 'inline-flex', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 500,
                    background: item.skill_req_pct >= 90 ? 'var(--red-bg)' : item.skill_req_pct >= 75 ? 'var(--amb-bg)' : 'var(--grn-bg)',
                    color: item.skill_req_pct >= 90 ? 'var(--red)' : item.skill_req_pct >= 75 ? 'var(--amb)' : 'var(--grn)',
                  }}>{item.skill_req_pct}%</span>
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--text-secondary)', fontSize: 11.5 }}>{item.machine_type || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '9px 14px', borderTop: '0.5px solid var(--border-md)', background: 'var(--bg-secondary)', fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <span><strong style={{ color: 'var(--text-primary)' }}>Machines: </strong>{Object.entries(machineMap).map(([k, v]) => `${k} × ${v}`).join(' · ') || '—'}</span>
        <span><strong style={{ color: 'var(--text-primary)' }}>Skill groups: </strong>{Object.entries(groupMap).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</span>
      </div>
    </div>
  );
}

// ── Saved line design detail table (loaded from API) ─────────────────────────
function SavedLdDetail({ ld, onClose, onDeploy }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/line-design/${ld.id}`)
      .then(({ data }) => setDetail(data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [ld.id]);

  const exportExcel = () => {
    if (!detail) return;
    const rows = detail.items.map((item, idx) => ({
      '#': idx + 1, 'Operation': item.operation_name,
      'SAM': parseFloat(item.sam).toFixed(3), 'Operators': item.operators_count,
      'Output/hr': Math.round(parseFloat(item.output_per_hr)), 'Skill Req%': item.skill_req_pct,
      'Machine': item.machine_type || '—', 'Bottleneck': item.is_bottleneck ? 'Yes' : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Line Design');
    XLSX.writeFile(wb, `line-design-${ld.ob_number}-${ld.manpower}op.xlsx`);
  };

  const eff = parseFloat(ld.efficiency || 0);

  const machineMap = {};
  const groupMap = {};
  if (detail) {
    detail.items.forEach(it => {
      if (it.machine_type) machineMap[it.machine_type] = (machineMap[it.machine_type] || 0) + Math.ceil(parseFloat(it.operators_count) || 0);
    });
  }

  return (
    <div style={{ border: '0.5px solid var(--acc)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)', marginBottom: 8 }}>
      {/* Detail header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '0.5px solid var(--border-md)', background: 'var(--acc-bg)' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--acc-t)' }}>{ld.ob_number} · {ld.style_number}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
            {ld.manpower} operators · {ld.target_output_day} pcs/day ·{' '}
            <strong style={{ color: eff >= 75 ? 'var(--grn)' : eff >= 65 ? 'var(--amb)' : 'var(--red)' }}>{eff.toFixed(1)}% efficiency</strong>
            {' · '}Bottleneck: <strong>{ld.bottleneck_operation || '—'}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" onClick={exportExcel} disabled={!detail}>Export Excel</Btn>
          <Btn size="sm" variant="primary" onClick={onDeploy}>Deploy →</Btn>
          <Btn size="sm" onClick={onClose}>Close</Btn>
        </div>
      </div>

      {/* Items table */}
      {loading ? (
        <div style={{ padding: 20 }}><Loading /></div>
      ) : !detail ? (
        <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Failed to load detail.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  {['#', 'Operation', 'SAM', 'Operators', 'Output/hr', 'Skill req%', 'Machine'].map((h, i) => (
                    <th key={i} style={{ padding: '7px 10px', borderBottom: '0.5px solid var(--border-md)', textAlign: i === 0 ? 'center' : 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '0.5px solid var(--border)', background: item.is_bottleneck ? '#FFF5F5' : '' }}>
                    <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: 10.5, color: 'var(--text-tertiary)' }}>{idx + 1}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                      {item.is_bottleneck && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E24B4A', display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} />}
                      {item.is_clubbed && item.clubbed_operation_name ? `${item.operation_name} + ${item.clubbed_operation_name}` : item.operation_name}
                      {item.is_clubbed && <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--acc-bg)', color: 'var(--acc)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>🔗 Clubbed</span>}
                      {item.is_bottleneck && <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--red-bg)', color: 'var(--red)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>Bottleneck</span>}
                    </td>
                    <td style={{ padding: '7px 10px' }}>{parseFloat(item.sam).toFixed(2)}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 500, textAlign: 'center' }}>{item.operators_count}</td>
                    <td style={{ padding: '7px 10px', color: item.is_bottleneck ? 'var(--red)' : 'var(--text-primary)', fontWeight: item.is_bottleneck ? 500 : 400 }}>
                      {Math.round(parseFloat(item.output_per_hr))}/hr
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{
                        display: 'inline-flex', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 500,
                        background: item.skill_req_pct >= 90 ? 'var(--red-bg)' : item.skill_req_pct >= 75 ? 'var(--amb-bg)' : 'var(--grn-bg)',
                        color: item.skill_req_pct >= 90 ? 'var(--red)' : item.skill_req_pct >= 75 ? 'var(--amb)' : 'var(--grn)',
                      }}>{item.skill_req_pct}%</span>
                    </td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-secondary)', fontSize: 11.5 }}>{item.machine_type || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Summary strip */}
          <div style={{ padding: '9px 14px', borderTop: '0.5px solid var(--border-md)', background: 'var(--bg-secondary)', fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span><strong style={{ color: 'var(--text-primary)' }}>Machines: </strong>{Object.entries(machineMap).map(([k, v]) => `${k} × ${v}`).join(' · ') || '—'}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Saved line design summary card ───────────────────────────────────────────
function SavedLdCard({ ld, onAssign, onDelete }) {
  const [open, setOpen] = useState(false);
  const eff = parseFloat(ld.efficiency || 0);
  const effColor = eff >= 75 ? 'green' : eff >= 65 ? 'amber' : 'red';

  if (open) {
    return (
      <SavedLdDetail
        ld={ld}
        onClose={() => setOpen(false)}
        onDeploy={() => onAssign(ld)}
      />
    );
  }

  return (
    <div style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500 }}>{ld.ob_number} · {ld.style_number}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
          {ld.manpower} operators · {ld.target_output_day} pcs/day · {eff.toFixed(1)}% efficiency · Bottleneck: {ld.bottleneck_operation || '—'}
        </div>
      </div>
      <Badge type={effColor}>{eff.toFixed(1)}%</Badge>
      <Badge type={ld.status === 'Active' ? 'green' : 'gray'}>{ld.status}</Badge>
      <Btn size="sm" onClick={() => setOpen(true)}>Open</Btn>
      <Btn size="sm" variant="primary" onClick={() => onAssign(ld)}>Assign line →</Btn>
      <Btn size="sm" onClick={() => onDelete(ld.id)} style={{ color: 'var(--red)', borderColor: 'var(--red)', background: 'var(--red-bg)' }}>Delete</Btn>
    </div>
  );
}

export default function LineDesign() {
  const location = useLocation();
  const navigate = useNavigate();

  const [obs, setObs] = useState([]);
  const [savedLds, setSavedLds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [divisions, setDivisions] = useState([]);
  const [factories, setFactories] = useState([]);
  const [allFactories, setAllFactories] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedOb, setSelectedOb] = useState(location.state?.ob_id ? String(location.state.ob_id) : '');
  const [mpMin, setMpMin] = useState(20);
  const [mpMax, setMpMax] = useState(28);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genError, setGenError] = useState('');
  const [manualModal, setManualModal] = useState(false);
  const [manualOps, setManualOps] = useState([]);
  const [clubModal, setClubModal] = useState(null); // { opCount, suggestions }
  const [clubSelection, setClubSelection] = useState([]); // indices selected in manual modal to club
  const [deployedLines, setDeployedLines] = useState([]); // other lines available as cross-line club partners
  const [externalClubRow, setExternalClubRow] = useState(null); // index of the row currently picking a cross-line partner
  const [externalLineId, setExternalLineId] = useState(''); // selected partner shift_line_id
  const [externalLineDesign, setExternalLineDesign] = useState(null); // fetched partner line design (with items)
  const [externalOpId, setExternalOpId] = useState(''); // selected partner operation's ldi id
  const [ldSearch, setLdSearch] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Line design'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [obRes, ldRes, divRes, facRes] = await Promise.all([api.get('/ob'), api.get('/line-design'), api.get('/divisions'), api.get('/factories')]);
      setObs(obRes.data.filter(o => o.status === 'Active' || o.status === 'Draft'));
      if (Array.isArray(divRes.data)) setDivisions(divRes.data);
      if (Array.isArray(facRes.data)) { setFactories(facRes.data); setAllFactories(facRes.data); }
      setSavedLds(ldRes.data);
      if (Array.isArray(divRes.data)) setDivisions(divRes.data);
      if (Array.isArray(facRes.data)) { setFactories(facRes.data); setAllFactories(facRes.data); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const handleDeleteLd = async (id) => {
    if (!window.confirm('Delete this line design? This cannot be undone. (Blocked if any deployment still uses it.)')) return;
    try {
      await api.delete(`/line-design/${id}`);
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Error deleting line design'); }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (selectedDivision) {
      setFactories(allFactories.filter(f => String(f.division_id) === String(selectedDivision)));
      setSelectedFactory('');
    } else {
      setFactories(allFactories);
    }
  }, [selectedDivision, allFactories]);
  useEffect(() => { if (location.state?.ob_id) setSelectedOb(String(location.state.ob_id)); }, [location.state]);

  const handleGenerate = async () => {
    if (!selectedOb) { setGenError('Select an OB first'); return; }
    if (parseInt(mpMin) < 1 || parseInt(mpMax) < parseInt(mpMin)) { setGenError('Invalid manpower range'); return; }
    setGenerating(true); setGenError(''); setScenarios([]); setActiveScenario(null);
    try {
      // Check if manpower < number of OB operations — suggest clubbing
      const obDetail = await api.get(`/ob/${selectedOb}`);
      const items = obDetail.data?.items || [];
      if (items.length > 0 && parseInt(mpMin) < items.length) {
        if (true) {
          // Bottleneck SAM = max SAM in OB
          const bottleneckSam = Math.max(...items.map(i => parseFloat(i.sam)));
          const threshold = bottleneckSam * 0.90;
          // Find pairs where sam1 + sam2 <= threshold
          const suggestions = [];
          for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
              const combined = parseFloat(items[i].sam) + parseFloat(items[j].sam);
              if (combined <= threshold) {
                suggestions.push({
                  op1: items[i].operation_name,
                  op2: items[j].operation_name,
                  sam1: parseFloat(items[i].sam),
                  sam2: parseFloat(items[j].sam),
                  combined: parseFloat(combined.toFixed(3)),
                  sameGroup: items[i].fundamental_group_id && items[i].fundamental_group_id === items[j].fundamental_group_id
                });
              }
            }
          }
          // Sort: same group first, then by combined SAM descending
          suggestions.sort((a, b) => (b.sameGroup - a.sameGroup) || (b.combined - a.combined));
          setClubModal({ opCount: items.length, mpMin: parseInt(mpMin), suggestions: suggestions.slice(0, 10) });
          setGenerating(false);
          return;
        }
      }

      const { data } = await api.post('/line-design/generate', {
        ob_id: selectedOb,
        manpower_min: parseInt(mpMin),
        manpower_max: parseInt(mpMax),
      });
      setScenarios(data.scenarios);
      if (data.scenarios.length) setActiveScenario(data.scenarios[0]);
    } catch (e) { setGenError(e.response?.data?.error || 'Error generating scenarios'); }
    setGenerating(false);
  };

  const handleGenerateForced = async () => {
    setClubModal(null);
    setGenerating(true); setGenError(''); setScenarios([]); setActiveScenario(null);
    try {
      const { data } = await api.post('/line-design/generate', {
        ob_id: selectedOb,
        manpower_min: parseInt(mpMin),
        manpower_max: parseInt(mpMax),
      });
      setScenarios(data.scenarios);
      if (data.scenarios.length) setActiveScenario(data.scenarios[0]);
    } catch (e) { setGenError(e.response?.data?.error || 'Error generating scenarios'); }
    setGenerating(false);
  };

  const handleOpenManual = () => {
    if (!activeScenario) return;
    // Pre-fill with current scenario operator counts
    setManualOps(activeScenario.items.map(item => ({
      ...item,
      manual_ops: item.ops_count,
      is_clubbed: item.is_clubbed || false,
      clubbed_index: item.clubbed_index != null ? item.clubbed_index : null,
      club_external: item.club_external || null,
    })));
    setClubSelection([]);
    setManualModal(true);
    // Load other currently-deployed lines as potential cross-line club partners
    const currentOb = obs.find(o => String(o.id) === String(selectedOb));
    api.get('/line-design/deployed-lines', { params: { factory_id: currentOb?.factory_id || undefined } })
      .then(({ data }) => setDeployedLines(data))
      .catch(() => setDeployedLines([]));
  };

  const calcManualScenario = (ops) => {
    const opMins = 480;
    const totalSam = ops.reduce((s, o) => s + parseFloat(o.sam), 0);
    const totalManpower = ops.reduce((s, o) => s + (parseFloat(o.manual_ops) || 0.5), 0);

    // Clubbing only decides WHO staffs the fractional part in Deployment (a shared
    // operator instead of a dedicated one) — it doesn't change capacity math here.
    // Output/hr is always operators_count x 60/SAM for every row.
    const items = ops.map((o, idx) => ({
      ...o,
      ops_count: parseFloat(o.manual_ops) || 1,
      output_hr: ((parseFloat(o.manual_ops) || 1) * 60) / parseFloat(o.sam),
      clubbed_partner_name: o.club_external
        ? `${o.club_external.target_operation_name} (Line ${o.club_external.target_line_number})`
        : (o.is_clubbed && o.clubbed_index != null ? ops[o.clubbed_index]?.operation_name : null),
      club_external: o.club_external || null,
    }));

    const target_hr = Math.min(...items.map(i => i.output_hr));
    const target_day = Math.round(target_hr * (opMins / 60));
    const efficiency = totalManpower > 0
      ? Math.round(((target_day * totalSam) / (totalManpower * opMins)) * 10000) / 100
      : 0;
    const botItem = items.find(i => Math.abs(i.output_hr - target_hr) < 0.001);
    return {
      manpower: totalManpower,
      efficiency,
      target_output_hr: Math.round(target_hr),
      target_output_day: target_day,
      bottleneck_operation: botItem?.operation_name || '',
      total_sam: totalSam,
      is_manual: true,
      items: items.map(i => ({
        ...i,
        skill_req_pct: target_hr > 0 ? Math.round((target_hr / i.output_hr) * 100) : 0,
        is_bottleneck: Math.abs(i.output_hr - target_hr) < 0.001,
      }))
    };
  };

  const openExternalClub = (idx) => {
    setExternalClubRow(idx);
    setExternalLineId('');
    setExternalLineDesign(null);
    setExternalOpId('');
  };

  const selectExternalLine = async (shiftLineId) => {
    setExternalLineId(shiftLineId);
    setExternalOpId('');
    const line = deployedLines.find(l => String(l.shift_line_id) === String(shiftLineId));
    if (!line) { setExternalLineDesign(null); return; }
    try {
      const { data } = await api.get(`/line-design/${line.line_design_id}`);
      setExternalLineDesign({ ...data, line_number: line.line_number });
    } catch (e) { setExternalLineDesign(null); }
  };

  const confirmExternalClub = () => {
    if (externalClubRow == null || !externalOpId || !externalLineDesign) return;
    const targetOp = externalLineDesign.items.find(i => String(i.id) === String(externalOpId));
    setManualOps(prev => {
      const updated = [...prev];
      updated[externalClubRow] = {
        ...updated[externalClubRow],
        is_clubbed: true,
        clubbed_index: null,
        club_external: {
          target_ldi_id: parseInt(externalOpId),
          target_line_number: externalLineDesign.line_number,
          target_operation_name: targetOp?.operation_name || '',
        }
      };
      return updated;
    });
    setExternalClubRow(null);
  };

  const cancelExternalClub = () => setExternalClubRow(null);

  const toggleClubSelect = (idx) => {
    setClubSelection(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      if (prev.length >= 2) return [prev[1], idx];
      return [...prev, idx];
    });
  };

  // Club links 2 rows so their FRACTIONAL remainder (e.g. the .5 in 1.5) shares one
  // operator in Deployment. Real entered values (1.5, 1.5, etc.) are preserved —
  // clubbing never overwrites operators_count.
  const handleClubSelected = () => {
    if (clubSelection.length !== 2) return;
    const [a, b] = clubSelection;
    setManualOps(prev => {
      const updated = [...prev];
      updated[a] = { ...updated[a], is_clubbed: true, clubbed_index: b };
      updated[b] = { ...updated[b], is_clubbed: true, clubbed_index: a };
      return updated;
    });
    setClubSelection([]);
  };

  const handleUnclub = (idx) => {
    setManualOps(prev => {
      const updated = [...prev];
      const partnerIdx = updated[idx].clubbed_index;
      updated[idx] = { ...updated[idx], is_clubbed: false, clubbed_index: null, club_external: null };
      if (partnerIdx != null && updated[partnerIdx]) {
        updated[partnerIdx] = { ...updated[partnerIdx], is_clubbed: false, clubbed_index: null };
      }
      return updated;
    });
  };

  const handleApplyManual = () => {
    const sc = calcManualScenario(manualOps);
    setActiveScenario(sc);
    setManualModal(false);
  };

  const handleSaveScenario = async () => {
    if (!activeScenario) return;
    setSaving(true);
    try {
      await api.post('/line-design/save', { ob_id: selectedOb, scenario: { ...activeScenario, is_manual: activeScenario.is_manual || false } });
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Error saving'); }
    setSaving(false);
  };

  const handleAssignScenario = async () => {
    if (!activeScenario) return;
    setSaving(true);
    try {
      const { data } = await api.post('/line-design/save', { ob_id: selectedOb, scenario: { ...activeScenario, is_manual: activeScenario.is_manual || false } });
      fetchAll();
      navigate('/deployment', { state: { line_design_id: data.id } });
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    setSaving(false);
  };

  const handleAssign = (ld) => {
    navigate('/deployment', { state: { line_design_id: ld.id } });
  };

  const filteredLds = savedLds.filter(ld => {
    // Apply division/factory filter via OB lookup
    const ob = obs.find(o => o.id === ld.ob_id);
    if (selectedDivision && ob?.division_id && String(ob.division_id) !== String(selectedDivision)) return false;
    if (selectedFactory && ob?.factory_id && String(ob.factory_id) !== String(selectedFactory)) return false;
    if (!ldSearch) return true;
    return (
      ld.style_number?.toLowerCase().includes(ldSearch.toLowerCase()) ||
      ld.ob_number?.toLowerCase().includes(ldSearch.toLowerCase())
    );
  });

  const inpStyle = { padding: '5px 9px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 7, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit' };

  // ─── Clubbing suggestions modal ────────────────────────────────────────────
  const ClubbingModal = () => {
    if (!clubModal) return null;
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{
          background: 'var(--bg-primary)', borderRadius: 14, padding: 24,
          width: 560, maxHeight: '80vh', overflow: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Operations need to be clubbed</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            This OB has <strong>{clubModal.opCount} operations</strong> but you selected <strong>{clubModal.mpMin} operators</strong>.
            Please update the Operation Bulletin to club some operations together.
            Below are suggested pairs where combined SAM ≤ 90% of bottleneck SAM:
          </div>
          {clubModal.suggestions.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              No suitable clubbing pairs found. Try increasing manpower or reviewing SAM values.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Operation 1</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Operation 2</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Combined SAM</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>Group</th>
                </tr>
              </thead>
              <tbody>
                {clubModal.suggestions.map((s, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border)', background: s.sameGroup ? 'var(--grn-bg)' : 'transparent' }}>
                    <td style={{ padding: '7px 10px' }}>{s.op1} <span style={{ color: 'var(--text-tertiary)' }}>({s.sam1})</span></td>
                    <td style={{ padding: '7px 10px' }}>{s.op2} <span style={{ color: 'var(--text-tertiary)' }}>({s.sam2})</span></td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--acc)' }}>{s.combined}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      {s.sameGroup ? <span style={{ color: 'var(--grn)', fontSize: 11 }}>Same group ✓</span> : <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18, gap: 8 }}>
            <button onClick={() => { setClubModal(null); setGenerating(false); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
              Go back and update OB
            </button>
            <button onClick={() => { setClubModal(null); handleGenerateForced(); }}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--acc)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Generate anyway
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 18 }}>
      <ClubbingModal />

      {/* Manual Override Modal */}
      {manualModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: 14, padding: 24, width: 560, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Manual Line Design</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Enter operator count per operation (decimals allowed, e.g. 1.5). If an operation needs a fractional extra operator, select it and a partner operation below, then click Club — the two share ONE operator for that leftover portion in Deployment; each still gets its own dedicated operator(s) for the whole part.</div>
            {clubSelection.length === 2 && (
              <div style={{ marginBottom: 10 }}>
                <button onClick={handleClubSelected} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'var(--acc)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Club selected 2 operations
                </button>
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  {['Club', 'Operation', 'SAM', 'Operators'].map((h, i) => (
                    <th key={i} style={{ padding: '7px 10px', textAlign: i === 0 ? 'center' : 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '0.5px solid var(--border-md)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {manualOps.map((op, idx) => (
                  <React.Fragment key={idx}>
                  <tr style={{ borderBottom: '0.5px solid var(--border)', background: op.is_clubbed ? 'var(--acc-bg)' : 'transparent' }}>
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      {!op.is_clubbed && (
                        <input type="checkbox" checked={clubSelection.includes(idx)} onChange={() => toggleClubSelect(idx)} />
                      )}
                    </td>
                    <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                      {op.operation_name}
                      {op.is_clubbed && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--acc)', color: '#fff', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>
                          🔗 shares extra operator with {op.club_external
                            ? `${op.club_external.target_operation_name} (Line ${op.club_external.target_line_number})`
                            : manualOps[op.clubbed_index]?.operation_name}
                        </span>
                      )}
                      {!op.is_clubbed && (parseFloat(op.manual_ops) % 1 !== 0) && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--red-bg)', color: 'var(--red)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>
                          ⚠ fractional — club within this line, or with another line
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-secondary)' }}>{parseFloat(op.sam).toFixed(3)}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <input type="number" min={0.5} step={0.5} value={op.manual_ops}
                          onChange={e => {
                            const updated = [...manualOps];
                            updated[idx] = { ...updated[idx], manual_ops: Math.max(0.5, parseFloat(e.target.value) || 1) };
                            setManualOps(updated);
                          }}
                          style={{ width: 60, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-md)', fontSize: 13, textAlign: 'center' }}
                        />
                        {op.is_clubbed && (
                          <button onClick={() => handleUnclub(idx)} style={{ padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--border)', background: 'transparent', fontSize: 11, cursor: 'pointer' }}>Unclub</button>
                        )}
                        {!op.is_clubbed && (parseFloat(op.manual_ops) % 1 !== 0) && (
                          <button onClick={() => openExternalClub(idx)} style={{ padding: '3px 8px', borderRadius: 6, border: '0.5px solid var(--acc)', background: 'var(--acc-bg)', color: 'var(--acc)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                            Club with another line
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {externalClubRow === idx && (
                    <tr>
                      <td colSpan={4} style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderBottom: '0.5px solid var(--border)' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>Club "{op.operation_name}" with an operation on another line</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select value={externalLineId} onChange={e => selectExternalLine(e.target.value)}
                            style={{ padding: '5px 9px', fontSize: 12, borderRadius: 6, border: '0.5px solid var(--border-md)' }}>
                            <option value="">Select line…</option>
                            {deployedLines.map(l => (
                              <option key={l.shift_line_id} value={l.shift_line_id}>
                                Line {l.line_number} · {l.style_number} ({l.status})
                              </option>
                            ))}
                          </select>
                          {externalLineDesign && (
                            <select value={externalOpId} onChange={e => setExternalOpId(e.target.value)}
                              style={{ padding: '5px 9px', fontSize: 12, borderRadius: 6, border: '0.5px solid var(--border-md)' }}>
                              <option value="">Select operation…</option>
                              {externalLineDesign.items.map(i => (
                                <option key={i.id} value={i.id}>{i.operation_name} ({parseFloat(i.operators_count).toFixed(1)} ops)</option>
                              ))}
                            </select>
                          )}
                          <button onClick={confirmExternalClub} disabled={!externalOpId}
                            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: externalOpId ? 'var(--acc)' : 'var(--border-md)', color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: externalOpId ? 'pointer' : 'default' }}>
                            Confirm club
                          </button>
                          <button onClick={cancelExternalClub}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: 'transparent', fontSize: 11.5, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                        {externalLineDesign && (
                          <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                            Note: this adds 0.5 operators to the chosen operation on Line {externalLineDesign.line_number} and recalculates
                            that line's efficiency. If that line already has a deployment, a new vacant slot will be added — someone must
                            manually assign who fills it.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {/* Live preview */}
            {(() => {
              const preview = calcManualScenario(manualOps);
              const eff = preview.efficiency;
              return (
                <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 12 }}>
                  <strong>Preview: </strong>
                  {preview.manpower} operators · Output: <strong>{preview.target_output_day} pcs/day</strong> · 
                  Efficiency: <strong style={{ color: eff >= 75 ? 'var(--grn)' : eff >= 65 ? 'var(--amb)' : 'var(--red)' }}>{eff.toFixed(1)}%</strong> · 
                  Bottleneck: <strong>{preview.bottleneck_operation}</strong>
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setManualModal(false)} style={{ padding: '8px 18px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleApplyManual} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--acc)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Apply & Preview</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 2 }}>Line design <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{savedLds.length} saved</span></div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>Generate optimal operator deployment scenarios from an OB</div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          <option value="">All divisions</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={selectedFactory} onChange={e => setSelectedFactory(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          <option value="">All factories</option>
          {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>
      <div style={{ background: 'var(--bg-secondary)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Operation bulletin</div>
            <select value={selectedOb} onChange={e => setSelectedOb(e.target.value)} style={{ ...inpStyle, width: '100%' }}>
              <option value="">— select OB —</option>
              {obs
                .filter(o => {
                  if (selectedDivision && o.division_id && String(o.division_id) !== String(selectedDivision)) return false;
                  if (selectedFactory && o.factory_id && String(o.factory_id) !== String(selectedFactory)) return false;
                  return true;
                })
                .map(o => <option key={o.id} value={o.id}>{o.ob_number} · {o.style_number}{o.style_description ? ` · ${o.style_description}` : ''}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Min operators</div>
            <input type="number" value={mpMin} onChange={e => setMpMin(e.target.value)} style={{ ...inpStyle, width: '100%' }} min={1} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Max operators</div>
            <input type="number" value={mpMax} onChange={e => setMpMax(e.target.value)} style={{ ...inpStyle, width: '100%' }} min={1} />
          </div>
          <Btn variant="primary" onClick={handleGenerate} disabled={generating}>{generating ? 'Generating…' : 'Generate →'}</Btn>
        </div>
        {genError && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 10px', borderRadius: 6 }}>{genError}</div>}
      </div>

      {scenarios.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
            {scenarios.map(sc => (
              <ScenarioCard key={sc.manpower} scenario={sc} selected={activeScenario?.manpower === sc.manpower} onClick={() => setActiveScenario(sc)} />
            ))}
          </div>
          <ScenarioDetail scenario={activeScenario} onSave={handleSaveScenario} onAssign={handleAssignScenario} onManual={handleOpenManual} saving={saving} />
          <div style={{ height: 24 }} />
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Saved line designs</div>
        <input
          className="inp"
          placeholder="Search by style number…"
          value={ldSearch}
          onChange={e => setLdSearch(e.target.value)}
          style={{ width: 220 }}
        />
      </div>

      {loading ? <Loading /> : filteredLds.length === 0
        ? <EmptyState message={savedLds.length === 0 ? 'No saved line designs yet. Generate and save a scenario above.' : 'No results found.'} />
        : filteredLds.map(ld => <SavedLdCard key={ld.id} ld={ld} onAssign={handleAssign} onDelete={handleDeleteLd} />)
      }
    </div>
  );
}